/**
 * Table ⇄ grid switch for ResponsiveTable's `view` prop.
 *
 * Lives beside that component because it exists to drive it: a page holds the
 * view in state, renders this to change it, and passes the same value down.
 *
 * DESKTOP ONLY by design — mobile always renders cards regardless of `view`, so
 * a toggle there would be a control that does nothing. Callers gate on
 * `useResponsive().isMobile`; the component does not hide itself, so a caller
 * with a different rule (e.g. tablet) stays free to choose.
 */
import { type ReactNode } from 'react';
import './ViewToggle.css';

export type TableView = 'table' | 'grid';

interface Props {
  view: TableView;
  onChange: (view: TableView) => void;
  className?: string;
}

const OPTIONS: { value: TableView; label: string; icon: ReactNode }[] = [
  {
    value: 'table',
    label: 'Table view',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <line x1="3" y1="7" x2="21" y2="7" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="17" x2="21" y2="17" />
      </svg>
    ),
  },
  {
    value: 'grid',
    label: 'Card view',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
        <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" /><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
      </svg>
    ),
  },
];

export function ViewToggle({ view, onChange, className }: Props) {
  return (
    <div className={`view-toggle ${className ?? ''}`.trim()} role="group" aria-label="List view">
      {OPTIONS.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={`view-toggle-btn${view === opt.value ? ' is-active' : ''}`}
          // aria-pressed, not aria-selected: these are toggle buttons, not tabs.
          aria-pressed={view === opt.value}
          aria-label={opt.label}
          title={opt.label}
          onClick={() => onChange(opt.value)}
        >
          {opt.icon}
        </button>
      ))}
    </div>
  );
}
